import React from "react";
import { Box, Button, Paper, styled, Table, TableContainer } from "@mui/material";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { Ischools } from "../interfaces/interface";
import { postRequest } from "../functions/api_functions";
import AlertMessage from "./alert_component";
import TwoLevelPieChart from "./charts/students_piechart";

const VisuallyHiddenInput = styled("input")({ clip: "rect(0 0 0 0)", clipPath: "inset(50%)", height: 1, overflow: "hidden", position: "absolute", bottom: 0, left: 0, whiteSpace: "nowrap", width: 1, });

interface ChildComponentProps {
  school?: Ischools;
  onClose?: () => void;
}

// Tipo de resposta da API
type UploadResponse = {
  status_code: number;
  receive: number;
  insert_count: number;
  update_count: number;
};

type PieChartResponse = {
  status_code: number;
  students: number;
  students_response: number;
  total_female: number;
  total_male: number;
  response_female: number;
  response_male: number;
  chart_html: string
};

const Students: React.FC<ChildComponentProps> = ({ school, onClose }) => {
  const [alertMessage, setAlertMessage] = React.useState<{ message: string; type: "success" | "error" | "info"; close: boolean } | null>(null);
  const [pieChartData, setPieChartData] = React.useState('');


  const mountPieChart = async () => {
    if (!school?.school_cnpj) {
      setAlertMessage({ message: "CNPJ da escola não foi definido.", type: "error", close: true });
      return;
    }

    try {
      const response = await postRequest<PieChartResponse>("/bar_mode_stack/", {
        body: { school_cnpj: String(school.school_cnpj) || "" },
        headers: {},
      });

      if (response.status_code === 200) {
        setPieChartData(response.chart_html);
      } else {
        setAlertMessage({
          message: `Erro ao montar pie chart: ${String(response.status_code)}`,
          type: "error",
          close: false,
        });
      }
    } catch (error) {
      setAlertMessage({
        message: `Erro na visualização de alunos: ${String(error)}`,
        type: "error",
        close: false,
      });
    }
  };

  React.useEffect(() => {
    mountPieChart();
  }, []);

  const uploadStudentsFiles = async (file: File) => {
    setAlertMessage({ message: "Aguarde...", type: "info", close: false });

    if (!school?.school_cnpj) {
      setAlertMessage({ message: "CNPJ da escola não foi definido.", type: "error", close: true });
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("school_cnpj", school.school_cnpj);

    try {
      const response = await postRequest<UploadResponse>("/upload_students/", {
        body: formData,
        headers: {},
      });

      const statusMessages: Record<number, { message: string; type: "success" | "error" | "info"; close: boolean }> = {
        200: {
          message: `Recebidos: ${response.receive} - Novos alunos cadastrados: ${response.insert_count} - Alunos atualizados: ${response.update_count}`,
          type: "success",
          close: false,
        },
      };

      const responseData = statusMessages[response.status_code] ?? {
        message: `Erro na API de alunos: ${response.status_code}`,
        type: "error",
        close: false,
      };

      setAlertMessage(responseData);

      if (response.status_code === 200) {
        mountPieChart();
      }

    } catch (error) {
      setAlertMessage({
        message: `Erro na inserção de alunos: ${String(error)}`,
        type: "error",
        close: false,
      });
    }
  };

  return (
    <>
      {alertMessage && (
        <AlertMessage key={alertMessage.message} message={alertMessage.message} type={alertMessage.type} auto_close={alertMessage.close} />
      )}
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100vh", }}>
        <Button component="label" role={undefined} variant="contained" tabIndex={-1} startIcon={<CloudUploadIcon />}>
          Carregar lista de alunos
          <VisuallyHiddenInput type="file" accept=".xlsx, .xls" onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) uploadStudentsFiles(file);
            }}
          />
        </Button>
        <TableContainer component={Paper} sx={{ width: "auto", height: "auto", overflow: "auto" }}>
          <Table sx={{ minWidth: 650 }} size="small" aria-label="a dense table"></Table>
        </TableContainer>
            {pieChartData ? (
                <iframe
                    title="Gráfico Plotly"
                    srcDoc={pieChartData}
                    style={{ width: '100%', height: '600px', border: 'none' }}
                />
            ) : (
                <p>Carregando gráfico...</p>
            )}
      </Box>
    </>
  );
};

export default Students;